// Supplier-commission statements — what each crew owes DriveCars, by month.
//
// The counterpart to Payouts. Payouts is money leaving the platform to a crew;
// this is money coming back from one, on the two commercial models (0241)
// where the customer pays the crew at pickup in cash and DriveCars bills its
// percentage afterwards.
//
// PER-CURRENCY, NEVER MERGED. A crew collecting in MAD and EUR has two
// statements a month and they are shown as two rows. Adding them would produce
// a number denominated in nothing, and an invoice nobody can pay.
//
// The lifecycle is open → issued → paid. `issued` means the document has been
// SENT, which is why the API refuses to change it afterwards and why a late
// accrual for that month rolls into the next statement instead.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { EmptyHint, PageHeader } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { usePager } from '@/lib/use-pager';
import {
  fetchVendorStatement,
  fetchVendorStatements,
  generateVendorStatements,
  issueVendorStatement,
  markVendorStatementPaid,
  type VendorStatementRow,
  type VendorStatementStatus,
} from '@/lib/admin-api';

const STATUS_FILTERS = ['all', 'open', 'issued', 'paid'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

// admin-ui's StatusPill has no entry for these three and would render every
// one of them the same neutral grey — on a money screen "sent" and "settled"
// must not look alike.
const STATUS_TONE: Record<VendorStatementStatus, { bg: string; ink: string }> = {
  open: { bg: '#fef3c7', ink: '#92400e' },
  issued: { bg: '#dbeafe', ink: '#1e40af' },
  paid: { bg: '#d1fae5', ink: '#065f46' },
};

function StatementStatus({ status }: { status: VendorStatementStatus }) {
  const c = STATUS_TONE[status];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize"
      style={{ backgroundColor: c.bg, color: c.ink }}
    >
      {status}
    </span>
  );
}

/** A cycle index is `year × 12 + (month − 1)`. An operator reads months. */
function cycleLabel(cycleIndex: number): string {
  const year = Math.floor(cycleIndex / 12);
  const month = cycleIndex % 12;
  return new Date(Date.UTC(year, month, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Money always carries its currency here — see the file header. */
function money(amount: number, currency: string): string {
  return `${amount.toFixed(2)} ${currency}`;
}

/** The rate travels as a FRACTION (0.10). Nobody bills at "0.1%". */
function pct(fraction: number): string {
  return `${Number((fraction * 100).toFixed(4))}%`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { timeZone: 'UTC' });
}

export function VendorStatements() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [openId, setOpenId] = useState<number | null>(null);
  const { page, limit } = usePager();
  // ?vendorId= arrives from a crew's detail page, so "open this crew's
  // statements" lands already narrowed rather than on every crew's.
  const { vendorId } = useSearch({ strict: false }) as { vendorId?: number };
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['admin', 'vendor-statements', { status, vendorId, page, limit }],
    queryFn: ({ signal }) =>
      fetchVendorStatements(
        { status: status === 'all' ? undefined : status, vendorId, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'vendor-statements'] });
  };

  const issueMut = useMutation({ mutationFn: issueVendorStatement, onSuccess: invalidate });
  const paidMut = useMutation({ mutationFn: markVendorStatementPaid, onSuccess: invalidate });
  const generateMut = useMutation({
    mutationFn: generateVendorStatements,
    onSuccess: invalidate,
  });

  const rows = list.data?.rows ?? [];
  // The month in progress, computed SERVER-side so the screen does not depend
  // on the operator's own clock or timezone.
  const currentCycle = list.data?.currentCycleIndex ?? null;
  const mutationError = issueMut.error ?? paidMut.error ?? generateMut.error;

  // BILLING IS IN ARREARS: on 1 October ops bills SEPTEMBER. So the picker
  // defaults to the month just finished, not the one in progress. The month in
  // progress is still offered (a mid-month look at what is accruing is
  // legitimate) but nothing beyond it — a statement for a month that has not
  // happened is always empty and always reads as a bug. `null` until the list
  // loads, so the default is never computed from a guessed clock.
  const defaultCycle = currentCycle == null ? null : currentCycle - 1;
  const [pickedCycle, setPickedCycle] = useState<number | null>(null);
  const cycle = pickedCycle ?? defaultCycle;
  // Two years back is more history than anyone bills and still covers a
  // reconciliation that went badly.
  const cycleOptions =
    currentCycle == null
      ? []
      : Array.from({ length: 24 }, (_, i) => currentCycle - i);

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Finance"
        title="Supplier commission"
        sub="What each crew owes DriveCars on supplier-commission sales, rolled up per calendar month and per currency. Issue sends the statement; a statement already issued can no longer change."
        actions={
          cycle != null ? (
            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="statement-cycle"
                className="text-[12.5px] text-[color:var(--bo-mute)]"
              >
                Month
              </label>
              <select
                id="statement-cycle"
                value={String(cycle)}
                onChange={(e) => setPickedCycle(Number(e.target.value))}
                className="rounded-lg border border-[color:var(--bo-line)] bg-white px-2 py-1.5 text-[12.5px] focus-visible:ring-2 focus-visible:ring-[color:var(--bo-accent)]"
              >
                {cycleOptions.map((c) => (
                  <option key={c} value={String(c)}>
                    {cycleLabel(c)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="inline-flex items-center rounded-lg bg-[color:var(--bo-ink)] px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-black disabled:opacity-50"
                disabled={generateMut.isPending}
                onClick={() => generateMut.mutate(cycle)}
              >
                {generateMut.isPending ? 'Generating…' : 'Generate'}
              </button>
            </div>
          ) : null
        }
      />

      <div className="flex flex-wrap gap-1">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={status === s}
            onClick={() => setStatus(s)}
            className={`rounded-lg px-3 py-1.5 text-[12.5px] capitalize ${
              status === s
                ? 'bg-[color:var(--bo-ink)] font-semibold text-white'
                : 'text-[color:var(--bo-mute)] hover:bg-black/5'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {list.error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load statements: {(list.error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          {(mutationError as Error).message}
        </div>
      ) : null}

      {!list.isLoading && rows.length === 0 && !list.error ? (
        <EmptyHint>
          No statements yet — generate a month once its supplier-commission
          bookings have been confirmed.
        </EmptyHint>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--bo-line)] bg-white">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">
              <tr>
                <th className="px-3 py-2 text-start font-medium">Crew</th>
                <th className="px-3 py-2 text-start font-medium">Month</th>
                <th className="px-3 py-2 text-end font-medium">Bookings</th>
                <th className="px-3 py-2 text-end font-medium">Owed</th>
                <th className="px-3 py-2 text-start font-medium">Status</th>
                <th className="px-3 py-2 text-start font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[color:var(--bo-line)]">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="font-medium text-[color:var(--bo-accent)] hover:underline"
                      onClick={() => setOpenId(r.id)}
                    >
                      {r.vendorName ?? `Crew #${r.vendorId}`}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-[color:var(--bo-mute)]">
                    {cycleLabel(r.cycleIndex)}
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">{r.bookingCount}</td>
                  {/* The currency rides with the number. Always. */}
                  <td className="px-3 py-2 text-end font-semibold tabular-nums">
                    {money(r.amount, r.currency)}
                  </td>
                  <td className="px-3 py-2">
                    <StatementStatus status={r.status} />
                  </td>
                  <td className="px-3 py-2">
                    <StatementActions
                      row={r}
                      busy={
                        (issueMut.isPending && issueMut.variables === r.id) ||
                        (paidMut.isPending && paidMut.variables === r.id)
                      }
                      onIssue={() => issueMut.mutate(r.id)}
                      onPaid={() => paidMut.mutate(r.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId != null ? (
        <StatementDrawer id={openId} onClose={() => setOpenId(null)} />
      ) : null}
    </div>
  );
}

/** Only the transition the statement is actually in gets a button: offering
 *  "Mark paid" on an open statement invites a 409 the operator cannot act on. */
function StatementActions({
  row,
  busy,
  onIssue,
  onPaid,
}: {
  row: VendorStatementRow;
  busy: boolean;
  onIssue: () => void;
  onPaid: () => void;
}) {
  if (row.status === 'open') {
    return (
      <RowAction kind="primary" busy={busy} onClick={onIssue}>
        Issue
      </RowAction>
    );
  }
  if (row.status === 'issued') {
    return (
      <RowAction kind="primary" busy={busy} onClick={onPaid}>
        Mark paid
      </RowAction>
    );
  }
  return (
    <span className="text-[11.5px] text-[color:var(--bo-mute)]">
      Paid {row.paidAt ? fmtDate(row.paidAt) : ''}
    </span>
  );
}

/** The bookings behind the total. A statement a crew disputes is settled by
 *  reading these lines, so the base and the rate are shown, not just the
 *  commission they produced. */
function StatementDrawer({ id, onClose }: { id: number; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ['admin', 'vendor-statements', id],
    queryFn: ({ signal }) => fetchVendorStatement(id, signal),
  });

  return (
    <div className="rounded-xl border border-[color:var(--bo-line)] bg-white">
      <header className="flex items-center justify-between gap-3 border-b border-[color:var(--bo-line)] px-4 py-3">
        <h2 className="text-[13px] font-semibold">
          {detail.data
            ? `${detail.data.vendorName ?? `Crew #${detail.data.vendorId}`} · ${cycleLabel(detail.data.cycleIndex)} · ${detail.data.currency}`
            : 'Statement'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="text-[12px] text-[color:var(--bo-mute)] hover:underline"
        >
          Close
        </button>
      </header>

      {detail.isPending ? (
        <p className="px-4 py-6 text-[12.5px] text-[color:var(--bo-mute)]">Loading…</p>
      ) : detail.isError ? (
        <p role="alert" className="px-4 py-6 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load the statement lines.
        </p>
      ) : detail.data.lines.length === 0 ? (
        <p className="px-4 py-6 text-[12.5px] text-[color:var(--bo-mute)]">
          No bookings on this statement.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11px] uppercase tracking-wide text-[color:var(--bo-mute)]">
              <tr>
                <th className="px-3 py-2 text-start font-medium">Booking</th>
                <th className="px-3 py-2 text-start font-medium">Accrued</th>
                <th className="px-3 py-2 text-end font-medium">Trip base</th>
                <th className="px-3 py-2 text-end font-medium">Rate</th>
                <th className="px-3 py-2 text-end font-medium">Commission</th>
                <th className="px-3 py-2 text-end font-medium">Partner share</th>
              </tr>
            </thead>
            <tbody>
              {detail.data.lines.map((l) => (
                <tr key={l.ledgerId} className="border-t border-[color:var(--bo-line)]">
                  <td className="px-3 py-2 font-mono text-[11.5px]">{l.reference}</td>
                  <td className="px-3 py-2 text-[color:var(--bo-mute)]">
                    {fmtDate(l.accruedAt)}
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {money(l.baseAmount, l.currency)}
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">{pct(l.ratePct)}</td>
                  <td className="px-3 py-2 text-end font-semibold tabular-nums">
                    {money(l.amount, l.currency)}
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">
                    {money(l.partnerShare, l.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
